import PropTypes from 'prop-types';
import { forwardRef, useCallback, useRef, useState, useEffect } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
  WindowScroller,
} from 'react-virtualized';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Loader } from '@hivebrite/design-system/Loader';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';
import { toast } from '@hivebrite/design-system/Toast';

import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import deletePost from 'hb-react/frontoffice/apps/LiveFeed/utils/deletePost';
import getRailsType from 'hb-react/frontoffice/apps/LiveFeed/utils/getRailsType';
import toggleLike from 'hb-react/frontoffice/apps/LiveFeed/utils/toggleLike';
import ReportContent from 'hb-react/frontoffice/components/ReportContent';
import { getBlockedUserIds } from 'hb-react/frontoffice/utils/blockUser';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Filters from './Filters';
import Item from './Item';
import FromNotificationItem from '../FromNotificationItem';
import { END_OF_FEED_TYPE } from '../Landings/EndOfFeed/constants';

import 'react-virtualized/styles.css';

const Feed = forwardRef(
  (
    {
      displayFilters = false,
      fetchItems,
      filterNoResultText,
      handleUserCanNotAccessLiveFeed,
      initialFilter = { value: {} },
      itemMinHeight = 50,
      onTriggerTopPost,
      overscanRowCount = 5,
      scrollerRef,
      shouldTriggerAmplitudeOnlike = false,
      threshold = 5,
      version,
    },
    ref,
  ) => {
    const { flagEvNewsRevamp } = useFeatureFlag();
    const {
      config,
      amplitudeConfig,
      isItemCommentsExpanded,
      resetExpandedRegisteries,
    } = useLiveFeedContext();
    const outboxedFontTheme = useOutboxedFontTheme();
    const amplitude = amplitudeLogger(amplitudeConfig);
    const [currentFilter, setCurrentFilter] = useState(initialFilter);
    const [currentNewItem, setCurrentNewItem] = useState(null);
    const [hiddenUserIds, setHiddenUserIds] = useState(getBlockedUserIds());
    const [isFetching, setIsFetching] = useState(false);
    const [items, setItems] = useState(null);
    const [nextPage, setNextPage] = useState(1);
    const [reportedContent, setReportedContent] = useState(null);
    const [totalItems, setTotalItems] = useState(Infinity);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [itemBeingDeletedId, setItemBeingDeletedId] = useState(null);

    const wrapperRef = useRef();
    const measurementCacheRef = useRef(
      new CellMeasurerCache({
        fixedWidth: true,
        minHeight: itemMinHeight,
      }),
    );

    const rowRenderer = useCallback(
      ({ index, key, style, isVisible, parent }) => {
        const item = items[index];

        return (
          <CellMeasurer
            cache={measurementCacheRef.current}
            columnIndex={0}
            key={key}
            parent={parent}
            rowIndex={index}
          >
            {({ measure, registerChild }) => (
              <Box
                ref={registerChild}
                display="block"
                pb={5}
                px={{ _: item.type === END_OF_FEED_TYPE ? 4 : 0, m: 0 }}
                style={style}
              >
                <Item
                  hiddenUserIds={hiddenUserIds}
                  item={item}
                  displayCommentsByDefault={isItemCommentsExpanded(
                    item.id,
                    item.type,
                  )}
                  measure={measure}
                  onDeleteItem={handleDeleteContent}
                  onDeleteVirtualItem={handleDeleteVirtualContent}
                  onReportContent={handleReportContent}
                  onSetHiddenUserIds={handleSetHiddenUserIds}
                  onTogglePostLike={handleTogglePostLike}
                  onUpdateItem={handleUpdateItem}
                  isRowVisibleOrOutOfView={isVisible}
                  itemBeingDeletedId={itemBeingDeletedId}
                  parentType={config.type}
                />
              </Box>
            )}
          </CellMeasurer>
        );
      },
      [items, hiddenUserIds, itemBeingDeletedId],
    );

    const handleUpdateItem = useCallback(
      ({ newItem, triggerTopPost = false, isItemPartial = false } = {}) => {
        const itemIndex = items.findIndex(
          (item) => item.id === newItem.id && item.type === newItem.type,
        );

        if (
          currentNewItem &&
          newItem.id === currentNewItem.id &&
          newItem.type === currentNewItem.type
        ) {
          setCurrentNewItem((originalItem) =>
            isItemPartial
              ? {
                  ...originalItem,
                  ...newItem,
                }
              : newItem,
          );
        } else {
          setCurrentNewItem(null);
        }

        if (itemIndex !== -1) {
          let item = newItem;

          if (isItemPartial) {
            const originalItem = items[itemIndex];
            const partialItem = newItem;

            item = { ...originalItem, ...partialItem };
          }

          items[itemIndex] = item;

          setItems([...items]);
        }

        if (triggerTopPost) {
          onTriggerTopPost?.({ newItem });
        }
      },
      [currentNewItem, items],
    );

    const handleDeleteItem = useCallback(() => {
      setItemBeingDeletedId(itemToDelete.postId);

      deletePost({
        postId: itemToDelete.postId,
        routes: config.routes,
        type: itemToDelete.type,
        topicId: itemToDelete.topicId,
      })
        .then(() => {
          if (
            currentNewItem &&
            itemToDelete.postId === currentNewItem.id &&
            itemToDelete.type === currentNewItem.type
          ) {
            setCurrentNewItem(null);
          }

          setItems(
            (items || []).filter(
              (item) =>
                item.id !== itemToDelete.postId ||
                item.type !== itemToDelete.type,
            ),
          );

          toast.success(__('Successfully destroyed'));
          setItemToDelete(null);
        })
        .catch(() => toast.error(__('Sorry, an error occurred')))
        .finally(() => {
          setItemBeingDeletedId(null);
        });
    }, [currentNewItem, items, config, itemToDelete]);
    const handleFetchItems = useCallback(
      async (
        { page, cleanItems, resetItems } = {
          page: null,
          cleanItems: false,
          resetItems: false,
        },
      ) => {
        if (!isFetching) {
          setIsFetching(true);

          if (cleanItems) {
            setItems(null);
          }

          page = page || nextPage;

          const { total, items: newItems } = await fetchItems({
            routes: config.routes,
            page,
            filters: currentFilter?.value,
            onUserCanNotAccessLiveFeed: handleUserCanNotAccessLiveFeed,
          });

          if (resetItems) {
            setItems(newItems);
          } else {
            setItems([...items, ...(newItems || [])]);
          }

          setNextPage(page + 1);
          setTotalItems(total);
          setIsFetching(false);

          // Clean useless roles for Accessibility
          const badGridRoleEl = document.querySelector('[role="grid"]');

          badGridRoleEl?.removeAttribute('aria-label');
          badGridRoleEl?.removeAttribute('role');
          document.querySelector('[role="rowgroup"]')?.removeAttribute('role');
        }
      },
      [items, nextPage, isFetching, currentFilter, config],
    );
    const handleLoadMoreRows = useCallback(
      (params) => {
        setCurrentNewItem(null);
        handleFetchItems(params);
      },
      [handleFetchItems],
    );
    const handleIsRowLoaded = useCallback(
      ({ index }) => !!items[index],
      [items],
    );
    const handleChangeFilters = useCallback(
      (item) => {
        setCurrentFilter(item);
        resetExpandedRegisteries();
      },
      [resetExpandedRegisteries],
    );

    const resetFilters = useCallback(() => {
      setCurrentFilter(initialFilter);
    }, [initialFilter]);

    const handleSetHiddenUserIds = useCallback(
      (hiddenUserIds) => setHiddenUserIds(hiddenUserIds),
      [setHiddenUserIds],
    );
    const handleReportContent = useCallback(
      (item) => setReportedContent(item),
      [setReportedContent],
    );
    const closeReportContent = useCallback(
      () => setReportedContent(null),
      [setReportedContent],
    );
    const handleDeleteContent = useCallback(
      (item) => setItemToDelete(item),
      [setItemToDelete],
    );
    const handleDeleteVirtualContent = useCallback(
      (item) => {
        setItems((items || []).filter(({ type }) => type !== item.type));
      },
      [items],
    );
    const closeDeleteContent = useCallback(
      () => setItemToDelete(null),
      [setItemToDelete],
    );

    const triggerAmplitudeOnLike = ({ likeId, type }) => {
      // We only want to log "Like" event, not "unlike"
      if (!likeId && shouldTriggerAmplitudeOnlike) {
        amplitude.track('LiveFeed|ContentLiked', {
          landingType: type,
        });
      }
    };

    const handleTogglePostLike = useCallback(
      ({ likeId, likeableId, type }) => {
        triggerAmplitudeOnLike({ likeId, type });

        return toggleLike({
          likeableId,
          likeId,
          routes: config.routes,
          type,
          setItems,
          flagEvNewsRevamp,
        });
      },
      [config, items, flagEvNewsRevamp],
    );

    const handleTogglePostLikeFromCurrentNewItem = useCallback(
      ({ likeId, likeableId, type }) => {
        triggerAmplitudeOnLike({ likeId, type });

        return toggleLike({
          likeableId,
          likeId,
          routes: config.routes,
          type,
          setCurrentNewItem,
          flagEvNewsRevamp,
        });
      },
      [config, currentNewItem, flagEvNewsRevamp],
    );

    useEffect(() => {
      if (ref) {
        // Share some functions in ref
        ref.current = {
          focus: () => wrapperRef.current.focus(),
          setCurrentNewItem,
        };
      }
    }, [items]);

    useEffect(() => {
      setCurrentNewItem(null);
      handleFetchItems({ page: 1, cleanItems: true, resetItems: true });
    }, [config, currentFilter, version]);

    useEffect(() => {
      amplitude.track('LiveFeed|Viewed');
    }, []);

    return (
      <>
        {reportedContent && (
          <ReportContent
            id={reportedContent?.id}
            type={getRailsType(reportedContent?.type, flagEvNewsRevamp)}
            onCloseModal={closeReportContent}
          />
        )}
        {itemToDelete && (
          <ConfirmationModal
            confirmButtonLabel={__('Delete post')}
            isLoading={!!itemBeingDeletedId}
            title={__('Delete post')}
            text={__(
              'Are you sure to delete your post? This action is irreversible.',
            )}
            onCloseModal={closeDeleteContent}
            onConfirmation={handleDeleteItem}
          />
        )}

        <Wrapper ref={wrapperRef}>
          {currentNewItem && (
            <Item
              // reset component when we have a new item
              key={currentNewItem.id}
              data-testid="new-post"
              hiddenUserIds={hiddenUserIds}
              isActive
              item={currentNewItem}
              itemBeingDeletedId={itemBeingDeletedId}
              onDeleteItem={handleDeleteContent}
              onDeleteVirtualItem={handleDeleteVirtualContent}
              onReportContent={handleReportContent}
              onSetHiddenUserIds={handleSetHiddenUserIds}
              onTogglePostLike={handleTogglePostLikeFromCurrentNewItem}
              onUpdateItem={handleUpdateItem}
              parentType={config.type}
            />
          )}
          <Box display="block" mt={currentNewItem && 4}>
            <FromNotificationItem
              hiddenUserIds={hiddenUserIds}
              onDeleteItem={handleDeleteContent}
              onReportContent={handleReportContent}
              onSetHiddenUserIds={handleSetHiddenUserIds}
              onTogglePostLike={handleTogglePostLike}
              onUpdateItem={handleUpdateItem}
              itemBeingDeletedId={itemBeingDeletedId}
            />
          </Box>

          <ScreenReadersContent>
            <h2>{__('All posts')}</h2>
          </ScreenReadersContent>

          {displayFilters && (
            <Box mt={8} mb={4} justifyContent="flex-end">
              <Filters onChange={handleChangeFilters} value={currentFilter} />
            </Box>
          )}

          {/* Display another message if the user has no content with ['all'] filter */}
          {items?.length === 0 &&
            (currentFilter === null ? (
              <Card>
                <Box justifyContent="center" font="h4-semibold">
                  {__('Welcome! Share the first post for your community!')}
                </Box>
              </Card>
            ) : (
              <ThemeProvider
                theme={outboxedFontTheme.themeName}
                fontBrightness={outboxedFontTheme.fontBrightness}
              >
                {/* filterNoResultText can be a component */}
                {filterNoResultText ? (
                  <Box>{filterNoResultText}</Box>
                ) : (
                  <Box justifyContent="center">
                    <SimpleCard
                      buttonProps={{
                        onClick: resetFilters,
                        children: __('Clear all filters'),
                      }}
                      cardProps={{ width: '100%' }}
                      title={__('No results found')}
                      description={__('Try applying another filter search')}
                      faIconProps={{
                        iconName: 'radar',
                      }}
                    />
                  </Box>
                )}
              </ThemeProvider>
            ))}
          {items && items.length !== 0 && (
            <InfiniteLoader
              isRowLoaded={handleIsRowLoaded}
              loadMoreRows={handleLoadMoreRows}
              rowCount={totalItems}
              threshold={threshold}
            >
              {({ onRowsRendered, registerChild }) => (
                <WindowScroller ref={scrollerRef}>
                  {({ height, onChildScroll, scrollTop }) => (
                    <AutoSizer disableHeight>
                      {({ width }) => (
                        <List
                          autoHeight
                          aria-readonly={null}
                          deferredMeasurementCache={measurementCacheRef.current}
                          height={height}
                          onRowsRendered={onRowsRendered}
                          onScroll={onChildScroll}
                          overscanRowCount={overscanRowCount}
                          ref={registerChild}
                          rowCount={items.length}
                          rowHeight={measurementCacheRef.current.rowHeight}
                          rowRenderer={rowRenderer}
                          scrollTop={scrollTop}
                          tabIndex={-1}
                          width={width}
                        />
                      )}
                    </AutoSizer>
                  )}
                </WindowScroller>
              )}
            </InfiniteLoader>
          )}
          {isFetching && (
            <Box justifyContent="center" mb={6}>
              <Loader size={40} />
            </Box>
          )}
        </Wrapper>
      </>
    );
  },
);

Feed.propTypes = {
  filterNoResultText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  displayFilters: PropTypes.bool,
  fetchItems: PropTypes.func.isRequired,
  handleUserCanNotAccessLiveFeed: PropTypes.func,
  initialFilter: PropTypes.shape({
    value: PropTypes.object,
  }),
  itemMinHeight: PropTypes.number,
  onTriggerTopPost: PropTypes.func,
  overscanRowCount: PropTypes.number,
  scrollerRef: PropTypes.object,
  shouldTriggerAmplitudeOnlike: PropTypes.bool,
  threshold: PropTypes.number,
  version: PropTypes.string,
};

export default Feed;

const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
  mt: 5,
}))`
  outline: 0;

  .ReactVirtualized__Grid.ReactVirtualized__List,
  .ReactVirtualized__Grid__innerScrollContainer {
    outline: 0;
    overflow: visible !important;
    z-index: 1;
  }
`;
